import axios from 'axios';

// Visual apps (special editions): a content package per edition, published by the server as
// `<base>/<key>/<key>.json` with its pictures under `<base>/<key>/images/`.
// The package is read with plain axios (no session header, so no CORS preflight), but the server must
// still send `Access-Control-Allow-Origin` for /visualApps. Until it does, the same path on this site's
// own origin is tried first (the CRA dev proxy in package.json, or an Amplify rewrite of /visualApps/<*> to the server).
export const VISUAL_APPS_BASE = (process.env.REACT_APP_VISUAL_APPS_BASE || 'https://ib.electronicvillage.org/visualApps').replace(/\/+$/, '');
const SAME_ORIGIN_BASE = '/visualApps';

// Editions that have a page in this app. `tripIds` are the trips the edition belongs to.
const EDITIONS = {
  emirates: { tripIds: [450] },
  goethe: { tripIds: [447, 487] }
};

export const isEdition = (key) => Object.prototype.hasOwnProperty.call(EDITIONS, key);
export const editionPath = (key) => `/special/${key}`;

// "https://…/visualApps/goethe/goethe.json" -> "goethe"
const keyFromLink = (link) => {
  const m = /\/visualApps\/([\w-]+)\//.exec(String(link || ''));
  return m ? m[1] : null;
};

// The edition a trip belongs to, or null. The server decides: isVisualAppAvailable false means none; otherwise its
// visualAppLabel, else the key inside isVisualAppLink, else (a trip that came without those fields) the trip id.
export const editionOf = (trip) => {
  if (!trip) return null;
  const available = trip.isVisualAppAvailable;
  if (available === false || available === 0 || available === '0') return null;
  const label = String(trip.visualAppLabel || '').replace(/-ar$/, '');
  if (isEdition(label)) return label;
  const linked = keyFromLink(trip.isVisualAppLink);
  if (isEdition(linked)) return linked;
  const id = Number(trip.tripId);
  return Object.keys(EDITIONS).find((key) => EDITIONS[key].tripIds.includes(id)) || null;
};

// A path inside a package ("images/hafeet.jpg") as a full address; absolute addresses pass through.
export const visualAppAsset = (key, path) =>
  /^(https?:)?\/\//.test(path) ? path : `${VISUAL_APPS_BASE}/${key}/${String(path).replace(/^\.?\//, '')}`;

const files = new Map();

const fetchFile = async (key, path) => {
  // this site's own /visualApps first (the dev proxy, or a host rewrite to the server): it needs no CORS header.
  // A host without that rewrite answers with its HTML page, which is rejected below, and the server address is tried.
  const bases = [...new Set([SAME_ORIGIN_BASE, VISUAL_APPS_BASE])];
  let lastError;
  for (const base of bases) {
    try {
      const { data } = await axios.get(`${base}/${key}/${path}`, { timeout: 120000 });
      // a missing file is answered with the site's HTML page and a 200
      if (typeof data !== 'object' || data === null) throw new Error(`Visual app file "${key}/${path}" is not published`);
      return data;
    } catch (e) {
      lastError = e;
    }
  }
  throw lastError;
};

// A JSON file of an edition ("book/pages.json"), loaded once per visit; a failure is forgotten so a retry fetches again.
export const loadVisualFile = (key, path) => {
  const id = `${key}/${path}`;
  if (!files.has(id)) {
    files.set(id, fetchFile(key, path).catch((e) => {
      files.delete(id);
      throw e;
    }));
  }
  return files.get(id);
};

// The edition's package (packages run to a megabyte, so it is loaded once).
export const loadVisualApp = (key) => loadVisualFile(key, `${key}.json`);

// { en, ar } text -> the reader's language, else Arabic (the manuscript's language), else whatever exists.
export const pickText = (value, lang) => {
  if (!value || typeof value !== 'object') return value || '';
  const own = value[lang];
  if (own && String(own).trim()) return own;
  return value.ar || value.en || '';
};
